import clsx from 'clsx'
import FormatCurrency from 'components/Common/FormatCurrency'
import { formatSaleUnitLabel } from 'hooks/useSaleUnitFormatting'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import type { ComponentProps, ReactNode } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Typography from '../Typography/Typography'
import PriceRowValueDiscount from './Value/PriceRowValueDiscount'
import BodyText from 'LuxKit/Typography/BodyText'

interface Props extends Utils.PropsWithDataAttributes {
  size: 'S' | 'M' | 'L'
  /**
   * Leading text
   * @example From $999.99
   */
  caption?: string
  /**
   * Main price
   */
  price: number
  /**
   * @default roundedDollar
   */
  priceFormat?: ComponentProps<typeof FormatCurrency>['format']
  /**
   * @default normal-contrast
   */
  priceColour?: 'normal-contrast' | 'lux-plus'
  /**
   * Node before the main price
   */
  priceLeading?: ReactNode
  /**
   * Node after the sale unit
   */
  priceTrailing?: ReactNode
  /**
   * @example $999.99 /room
   * @example $999.99 total
   *
   * A leading "/" will be prepended for non-"total"
   */
  saleUnit?: string
  /**
   * @default light-foreground-alt
   */
  saleUnitColour?: 'light-foreground-alt' | 'lux-plus'
  discountPercentage?: number
  discountType?: ComponentProps<typeof PriceRowValueDiscount>['type']
  signDisplay?: ComponentProps<typeof FormatCurrency>['signDisplay']
  className?: string
  /**
   * @default no-wrap
   */
  wrap?: 'word-wrap' | 'no-wrap'
  /**
   * @default heading
   * @description Style of the price text
   */
  priceStyle?: 'heading' | 'body-text'
}

const SIZES_HEADING_VARIANTS: Record<Props['size'], ComponentProps<typeof Heading>['variant']> = {
  S: 'heading6',
  M: 'heading5',
  L: 'heading4',
}

const SIZES_BODY_TEXT_VARIANTS: Record<Props['size'], ComponentProps<typeof BodyText>['variant']> = {
  S: 'medium',
  M: 'large',
  L: 'large',
}

const PriceContainer = styled(Typography)`
  display: flex;
  align-items: baseline;
  column-gap: 0.25em;
  flex-wrap: nowrap;

  &.word-wrap {
    flex-wrap: wrap;
  }
`

/**
 * This component shall not be used directly.
 * Please use `<PriceRowPrice>` or `<PriceRowPriceWithCaption>` instead.
 */
function PriceRowPrice(props: Props) {
  const intl = useIntl()
  const {
    size,
    caption,
    priceStyle = 'heading',
    price,
    priceFormat = 'roundedDollar',
    priceColour = 'normal-contrast',
    priceLeading,
    priceTrailing,
    saleUnit,
    saleUnitColour = 'light-foreground-alt',
    discountPercentage,
    discountType,
    signDisplay,
    wrap = 'no-wrap',
    className,
    ...rest
  } = props

  const saleUnitLabel = formatSaleUnitLabel(intl, saleUnit, { withLeadingSlash: true })

  return <PriceContainer
    {...rest}
    className={clsx(className, { 'word-wrap': wrap === 'word-wrap' })}
    wrap={wrap}
  >
    {!!caption && <Caption
      as="span"
      variant="medium"
      colour="light-foreground-alt"
      weight="normal"
      format="propercase"
    >
      {caption}
    </Caption>}

    {priceLeading}

    {priceStyle === 'heading' && (
      <Heading
        as="span"
        variant={SIZES_HEADING_VARIANTS[size]}
        colour={priceColour}
        wrap="no-wrap"
      >
        <FormatCurrency value={price} format={priceFormat} signDisplay={signDisplay} />
      </Heading>
    )}
    {priceStyle === 'body-text' && (
      <BodyText
        as="span"
        variant={SIZES_BODY_TEXT_VARIANTS[size]}
        colour={priceColour}
      >
        <FormatCurrency value={price} format={priceFormat} signDisplay={signDisplay} />
      </BodyText>
    )}

    {saleUnitLabel && <Caption
      as="span"
      variant="medium"
      colour={saleUnitColour}
      weight="normal"
      format="lowercase"
    >
      {saleUnitLabel}
    </Caption>}
    {priceTrailing}
    {!!discountPercentage && <PriceRowValueDiscount
      discountPercentage={discountPercentage}
      type={discountType}
    />}
  </PriceContainer>
}

const _PriceRowPrice = PriceRowPrice

export default _PriceRowPrice
